import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";

const OPTIONS = ["Bio", "Training", "Special Skills", "Custom"];

const ResumeTextDialog = ({
  open,
  onClose,
  initialData,
  existingSections = [],
  onSave,
}) => {
  const [sectionType, setSectionType] = useState("");
  const [heading, setHeading] = useState("");
  const [details, setDetails] = useState("");

  /* ---------------- CORRECT EDIT DETECTION ---------------- */
  const isEditing =
    initialData && typeof initialData.resumeTextDataId === "number";

  /* ---------------- PREFILL ---------------- */
  useEffect(() => {
    if (!open) return;

    if (isEditing) {
      const incomingHeading =
        typeof initialData.resumeTextHeading === "string"
          ? initialData.resumeTextHeading.trim()
          : "";

      const matchedOption = OPTIONS.find(
        (opt) => opt.toLowerCase() === incomingHeading.toLowerCase()
      );

      setSectionType(matchedOption || "Custom");
      setHeading(incomingHeading);
      setDetails(initialData.resumeTextDetails || "");
      return;
    }

    // ADD MODE
    setSectionType("");
    setHeading("");
    setDetails("");
  }, [open, isEditing, initialData]);

  /* ---------------- SECTION TYPE CHANGE ---------------- */
  const handleTypeChange = (e) => {
    const selectedType = e.target.value;
    setSectionType(selectedType);

    if (selectedType === "Custom") {
      setHeading("");
      setDetails("");
      return;
    }

    // predefined
    setHeading(selectedType);

    if (isEditing) {
      setDetails(details); // preserve existing details
    } else {
      setDetails("");
    }
  };

  /* ---------------- SAVE ---------------- */
  const handleSave = () => {
    if (!heading.trim() || !details.trim()) return;

    const payload = {
      resumeTextDataId: isEditing
        ? initialData.resumeTextDataId // 🔑 KEEP SAME ID
        : 0,
      resumeSectionId: isEditing ? initialData.resumeSectionId : 0,
      resumeTextHeading: heading.trim(),
      resumeTextDetails: details.trim(),
      sortOrder: isEditing ? initialData.sortOrder : 0,
      isInternal: isEditing ? initialData.isInternal : 1,
      activeFlag: true,
    };

    onSave(payload);
    handleClose();
  };

  /* ---------------- CLOSE ---------------- */
  const handleClose = () => {
    setSectionType("");
    setHeading("");
    setDetails("");
    onClose();
  };

  /* ---------------- UI ---------------- */
  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {isEditing ? "Edit Section" : "Add Section"}
      </DialogTitle>

      <DialogContent>
        {/* Section */}
        <FormControl fullWidth margin="dense">
          <InputLabel>Section</InputLabel>
          <Select
            value={sectionType}
            label="Section"
            onChange={handleTypeChange}
          >
            {OPTIONS.map((opt) => {
              const isUsed = existingSections.some(
                (s) =>
                  typeof s.resumeTextHeading === "string" &&
                  s.resumeTextHeading.toLowerCase() === opt.toLowerCase()
              );

              const isEditingSame =
                isEditing &&
                initialData.resumeTextHeading.toLowerCase() ===
                  opt.toLowerCase();

              const shouldDisable =
                opt !== "Custom" && isUsed && !isEditingSame;

              return (
                <MenuItem key={opt} value={opt} disabled={shouldDisable}>
                  {opt}
                  {isUsed && !isEditingSame && opt !== "Custom"
                    ? " (Already exists)"
                    : ""}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        {/* Heading */}
        <TextField
          margin="dense"
          label="Heading"
          fullWidth
          value={heading}
          disabled={sectionType !== "Custom"}
          onChange={(e) => setHeading(e.target.value)}
          placeholder="Enter custom section heading"
        />

        {/* Details */}
        <TextField
          margin="dense"
          label="Details"
          fullWidth
          multiline
          minRows={4}
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          placeholder={
            heading ? `Enter ${heading} details...` : "Enter details..."
          }
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!heading.trim() || !details.trim()}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResumeTextDialog;
